import { TablePaginationConfig } from "antd";
import { accountFinanceApi } from "api/account";
import { useNotificationContext } from "contexts/NotificationContext";
import {
  defaultAccountFinanceTableData,
  IAccountFinanceTableData,
} from "models/account";
import moment from "moment";
import { IAccountFinanceFilter } from "pages/AccountPages/AccountFinance";
import { useEffect, useState } from "react";

const startDate = new Date();
const endDate = new Date();
startDate.setHours(0, 0, 0, 0);
endDate.setHours(23, 59, 59, 999);
export default function useFinanceAccountDashboard() {
  const { showError } = useNotificationContext();
  const [data, setData] = useState<IAccountFinanceTableData>(
    defaultAccountFinanceTableData
  );
  const [dateFilter, setDateFilter] = useState<{
    startDate: string;
    endDate: string;
  }>({
    endDate: endDate.toISOString(),
    startDate: startDate.toISOString(),
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [data.page, dateFilter.endDate, dateFilter.startDate]);

  const fetchData = async () => {
    setData((prev) => ({ ...prev, loading: true }));
    const { data: response, error } = await accountFinanceApi(
      data.page,
      data.per_page,
      dateFilter.startDate,
      dateFilter.endDate
    );
    if (response?.data) {
      response.data = response.data.map((value) => ({
        ...value,
        key: value.id,
      }));
      setData((prev) => ({ ...prev, ...response, loading: false }));
    } else {
      showError(error!.message);
      setData((prev) => ({ ...prev, loading: false }));
    }
  };

  const handlePageChange = ({ current }: TablePaginationConfig) => {
    if (current) setData((prev) => ({ ...prev, page: current }));
  };

  const handleFilterChange = async (value: IAccountFinanceFilter) => {
    switch (value) {
      case "Today": {
        const startDate = new Date();
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date();
        endDate.setHours(23, 59, 59, 999);
        setDateFilter((prev) => ({
          ...prev,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        }));
        break;
      }
      case "Yesterday": {
        const startDate = moment().subtract(1, "days").startOf("day").toDate();
        const endDate = moment().subtract(1, "days").endOf("day").toDate();
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        setDateFilter((prev) => ({
          ...prev,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        }));
        break;
      }
      case "This Week": {
        // Start of the week (Monday) to now
        const startDate = moment().startOf("week").toDate();
        const endDate = new Date(); // Now
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        setDateFilter((prev) => ({
          ...prev,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        }));
        break;
      }
      case "Last Week": {
        // Last week (Monday to Sunday)
        const startDate = moment()
          .subtract(1, "weeks")
          .startOf("week")
          .toDate();
        const endDate = moment().subtract(1, "weeks").endOf("week").toDate();
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        setDateFilter((prev) => ({
          ...prev,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        }));
        break;
      }
      case "This Month": {
        // Start of the month to now
        const startDate = moment().startOf("month").toDate();
        const endDate = new Date(); // Now
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        setDateFilter((prev) => ({
          ...prev,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        }));
        break;
      }
      case "Last Month": {
        // Last month (1st to last day of the month)
        const startDate = moment()
          .subtract(1, "months")
          .startOf("month")
          .toDate();
        const endDate = moment().subtract(1, "months").endOf("month").toDate();
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        setDateFilter((prev) => ({
          ...prev,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        }));
        break;
      }
      case "This Year": {
        // Start of the year to now
        const startDate = moment().startOf("year").toDate();
        const endDate = new Date(); // Now
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        setDateFilter((prev) => ({
          ...prev,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        }));
        break;
      }

      case "Last Year": {
        // Last year (1st Jan to 31st Dec)
        const startDate = moment()
          .subtract(1, "years")
          .startOf("year")
          .toDate();
        const endDate = moment().subtract(1, "years").endOf("year").toDate();
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        setDateFilter((prev) => ({
          ...prev,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        }));
        break;
      }
      default: {
        showError(
          `Invalid filter value: ${value}. Please use one of the predefined filters.`
        );
        break;
      }
    }
  };

  return { data, handlePageChange, handleFilterChange };
}
